import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import { useAdmin } from "../../context/AdminContext";
import { ADMIN } from "../../lib/adminI18n";
import { ShoppingBag, Loader2, Lock, ChevronDown, AlertTriangle } from "lucide-react";

export default function AdminLogin() {
  const { lang } = useApp();
  const a = ADMIN[lang];
  const { login, isAuthed } = useAdmin();
  const navigate = useNavigate();
  const [authInfo, setAuthInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("ha_admin_auth_error");
    if (raw) {
      try { setAuthInfo(JSON.parse(raw)); } catch { /* ignore */ }
      sessionStorage.removeItem("ha_admin_auth_error");
    }
  }, []);

  if (isAuthed) return <Navigate to="/admin" replace />;

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success(a.done);
      navigate("/admin");
    } catch (err) {
      if (err.code === "not_admin") toast.error(a.notAdmin);
      else toast.error(err?.response?.status === 401 ? a.invalid : a.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-4">
      <div className="w-full max-w-sm animate-ha-up">
        <div className="text-center mb-6">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center shadow-lg shadow-primary/25 mb-3">
            <ShoppingBag className="h-7 w-7" />
          </div>
          <h1 className="font-head text-2xl font-extrabold">{a.title}</h1>
          <p className="text-sm text-muted-foreground">{a.subtitle}</p>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" /> {a.loginHint}
          </div>

          {authInfo && (
            <div data-testid="admin-auth-error" className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <div className="flex items-start gap-2 text-destructive font-semibold">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{authInfo.detail || (authInfo.authError === "not_admin" ? a.notAdmin : a.invalid)}</span>
              </div>
              {authInfo.email && <div className="mt-1 text-xs text-muted-foreground ps-6 break-all">{authInfo.email}</div>}
            </div>
          )}

          <button
            data-testid="admin-google-login"
            onClick={googleLogin}
            className="w-full h-12 rounded-xl border border-border bg-background hover:bg-accent font-semibold flex items-center justify-center gap-3 transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {lang === "sv" ? "Fortsätt med Google" : "المتابعة عبر Google"}
          </button>

          <button onClick={() => setShowPw((v) => !v)} className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1">
            {lang === "sv" ? "Eller logga in med lösenord" : "أو الدخول بكلمة مرور"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showPw ? "rotate-180" : ""}`} />
          </button>

          {showPw && (
            <form onSubmit={submit} className="space-y-3 pt-1 border-t border-border">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{a.email}</label>
                <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-primary/40" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{a.password}</label>
                <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-primary/40" />
              </div>
              <button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {a.loggingIn}</> : a.login}
              </button>
            </form>
          )}

          <button type="button" onClick={() => navigate("/")} className="w-full text-sm text-muted-foreground hover:text-foreground">
            {a.backToSite}
          </button>
        </div>
      </div>
    </div>
  );
}
